// Which card, on which platform (migration 073; Mitch, 2026-11-03: "If it was paid with a card, which card, on which
// platform. We need to ensure that we can keep things accountable especially for the larger purchases"). HHL's own ways
// to pay (`pay_account`: a name and the last 4 digits only, never a full number) and where a payment was made
// (`paid_via`). Payments (`payment.pay_account_id` / `paid_via`) and run costs and tooling paid outside
// (`paid_account_id` / `paid_via`) say both; a card payment must.
import { z } from "zod";

export const PAY_ACCOUNT_KINDS = ["card", "bank", "wise", "paypal", "other"] as const;
export type PayAccountKind = (typeof PAY_ACCOUNT_KINDS)[number];
export const PAY_ACCOUNT_KIND_LABELS: Record<PayAccountKind, string> = {
  card: "Card",
  bank: "Bank account",
  wise: "Wise",
  paypal: "PayPal",
  other: "Other",
};

/** Where a payment was made. */
export const PAID_VIA = ["wise", "bank", "vendor_site", "paypal", "alibaba", "aliexpress", "amazon", "other"] as const;
export type PaidVia = (typeof PAID_VIA)[number];
export const PAID_VIA_LABELS: Record<PaidVia, string> = {
  wise: "Wise",
  bank: "Bank wire",
  vendor_site: "The vendor's website",
  paypal: "PayPal",
  alibaba: "Alibaba",
  aliexpress: "AliExpress",
  amazon: "Amazon",
  other: "Somewhere else",
};

export const payAccountFields = z.object({
  name: z.string().trim().min(1, "Name it (Amex Business)").max(100),
  kind: z.enum(PAY_ACCOUNT_KINDS),
  last4: z
    .string()
    .trim()
    .regex(/^(\d{4})?$/, "The last 4 digits only, never the whole number"),
  active: z.boolean(),
  notes: z.string().trim().max(2000),
});

type Account = { name: string; last4: string };

/** "Amex Business ••1005", or just the name. */
export const payAccountText = (a: Account | null | undefined) => (a ? `${a.name}${a.last4 ? ` ••${a.last4}` : ""}` : "");

/** "Amex Business ••1005 on the vendor's website", "Wise", "Card" (an older payment that doesn't say which). */
export function paidWithText(o: { account?: Account | null; via?: string; method?: string }) {
  const acc = payAccountText(o.account);
  const via = o.via && o.via in PAID_VIA_LABELS ? PAID_VIA_LABELS[o.via as PaidVia] : "";
  if (acc && via && !(o.via === "wise" && /wise/i.test(acc))) return `${acc} · ${via}`;
  if (acc) return acc;
  if (via) return via;
  return o.method ? o.method[0].toUpperCase() + o.method.slice(1) : "";
}

/** A card payment says which card and where; a missing one is the reason (null when fine). */
export function paidWithProblem(o: { method: string; accountKind?: string | null; via?: string }): string | null {
  if (o.method !== "card") return null;
  if (!o.accountKind) return "Say which card paid it (Settings → Payment accounts adds one)";
  if (o.accountKind !== "card") return "That account isn't a card: pick the card that paid it";
  if (!o.via) return "Say where it was paid (the vendor's website, PayPal, Alibaba…)";
  return null;
}
